import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Search, ChevronRight, Heart, BadgeCheck, MapPin, Star, Package, Laptop, Shirt, Palette,
  Home as HomeIcon, Snowflake, Gem, Baby, Volleyball, HeartPulse, Car, Smartphone, Watch, Utensils, Sofa, Wrench, Leaf, PawPrint, Book, Gift,
} from "lucide-react";
import { api, mediaUrl } from "../lib/api";
import { useLang } from "../context/LangContext";
import { useCurrency } from "../context/CurrencyContext";

const WISH_KEY = "simashi_wish";
const readWish = () => {
  try { return JSON.parse(localStorage.getItem(WISH_KEY) || "[]"); } catch { return []; }
};

const ICONS = [
  [/laptop|electron|computer|phone/i, Laptop],
  [/shirt|fashion|apparel|cloth/i, Shirt],
  [/palette|beauty|cosmetic|sparkl/i, Palette],
  [/home|house|furniture|sofa/i, HomeIcon],
  [/snow|appliance|fridge/i, Snowflake],
  [/diamond|jewel|gem|watch/i, Gem],
  [/baby|kids|toy|mother|happy/i, Baby],
  [/football|sport|entertain/i, Volleyball],
  [/health|medical|medkit/i, HeartPulse],
  [/car|auto|vehicle/i, Car],
  [/phone|mobile/i, Smartphone],
  [/food|restaurant|kitchen/i, Utensils],
  [/tool|machine|industr/i, Wrench],
  [/leaf|agri|farm|plant/i, Leaf],
  [/pet|animal/i, PawPrint],
  [/book|education|office/i, Book],
  [/gift/i, Gift],
  [/watch/i, Watch],
  [/sofa/i, Sofa],
];
const catIcon = (icon = "", label = "") => {
  const hit = ICONS.find(([re]) => re.test(icon) || re.test(label));
  return hit ? hit[1] : Package;
};

export default function Categories() {
  const { t } = useLang();
  const { fmt } = useCurrency();
  const navigate = useNavigate();
  const [cats, setCats] = useState([]);
  const [sel, setSel] = useState(null);
  const [sub, setSub] = useState("");
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [wish, setWish] = useState(readWish);

  useEffect(() => {
    api("/categories")
      .then((list) => {
        const en = (list || []).filter((c) => c.enabled !== false).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        setCats(en);
        if (en.length) setSel(en[0].key);
      })
      .catch(() => setCats([]));
  }, []);

  useEffect(() => {
    setSub("");
  }, [sel]);

  useEffect(() => {
    if (!sel) return;
    setItems(null);
    let url = `/products?category=${encodeURIComponent(sel)}&limit=24`;
    if (sub) url += `&subcategory=${encodeURIComponent(sub)}`;
    api(url).then((r) => setItems(Array.isArray(r) ? r : r.items || [])).catch(() => setItems([]));
  }, [sel, sub]);

  const toggleWish = (e, id) => {
    e.preventDefault();
    const next = wish.includes(id) ? wish.filter((x) => x !== id) : [id, ...wish].slice(0, 60);
    setWish(next);
    localStorage.setItem(WISH_KEY, JSON.stringify(next));
  };

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/products?q=${encodeURIComponent(q.trim())}`);
  };

  const current = cats.find((c) => c.key === sel);
  const subs = (current?.children || []).filter((c) => c.enabled !== false);
  const color = current?.color || "#EA580C";

  return (
    <div data-testid="categories-page" className="mx-auto max-w-7xl px-4 pb-16 pt-5 sm:px-6">
      <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">{t("tab_categories")}</h1>

      <form onSubmit={submitSearch} role="search" className="mt-3 flex items-center gap-2 rounded-full bg-slate-100 px-4 py-3">
        <Search className="h-4.5 w-4.5 h-5 w-5 shrink-0 text-slate-400" />
        <input
          data-testid="category-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("home_search_ph")}
          className="w-full bg-transparent text-sm outline-none placeholder:text-slate-400"
        />
      </form>

      <div className="mt-4 flex items-start gap-3">
        {/* Left sidebar — category list */}
        <aside data-testid="category-sidebar" className="sticky top-16 max-h-[calc(100dvh-9rem)] w-[30%] max-w-[130px] shrink-0 space-y-1 overflow-y-auto pb-6 pr-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {cats.map((c) => {
            const Icon = catIcon(c.icon, c.label);
            const active = c.key === sel;
            return (
              <button
                key={c.key}
                data-testid={`cat-side-${c.key}`}
                onClick={() => setSel(c.key)}
                className={`flex w-full flex-col items-center gap-1.5 rounded-2xl px-1 py-3 text-center transition active:scale-95 ${active ? "bg-orange-50" : "hover:bg-slate-50"}`}
              >
                <span
                  className="flex h-11 w-11 items-center justify-center rounded-full transition"
                  style={active ? { backgroundColor: c.color || "#EA580C" } : { backgroundColor: `${c.color || "#EA580C"}1A` }}
                >
                  <Icon className="h-5 w-5" style={{ color: active ? "#fff" : c.color || "#EA580C" }} />
                </span>
                <span className={`text-[10px] leading-tight ${active ? "font-extrabold text-slate-900" : "font-medium text-slate-500"}`}>
                  {c.label}
                </span>
              </button>
            );
          })}
        </aside>

        {/* Right content */}
        <div className="min-w-0 flex-1">
          {current && (
            <Link
              data-testid="cat-banner"
              to={`/products?category=${encodeURIComponent(current.key)}`}
              className="relative flex items-center justify-between overflow-hidden rounded-2xl p-4 text-white shadow-md transition active:scale-[0.99]"
              style={{ background: `linear-gradient(115deg, ${color}, ${color}B3)` }}
            >
              <div>
                <p className="font-heading text-lg font-extrabold">{current.label}</p>
                <p className="mt-0.5 text-xs text-white/85">
                  {current.count ?? items?.length ?? 0} {t("items_t").toLowerCase()} • {t("view_all_products")}
                </p>
              </div>
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/25">
                <ChevronRight className="h-5 w-5" />
              </span>
            </Link>
          )}

          {subs.length > 0 && (
            <div className="-mx-1 mt-3 flex gap-2 overflow-x-auto px-1 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <button
                data-testid="subpill-all"
                onClick={() => setSub("")}
                className={`shrink-0 rounded-full px-4 py-1.5 text-xs font-bold transition active:scale-95 ${!sub ? "bg-teal-700 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
              >
                {t("grp_all")}
              </button>
              {subs.map((s) => (
                <button
                  key={s.key}
                  data-testid={`subpill-${s.key}`}
                  onClick={() => setSub(s.key)}
                  className={`shrink-0 rounded-full px-4 py-1.5 text-xs font-bold transition active:scale-95 ${sub === s.key ? "bg-teal-700 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          )}

          {items === null ? (
            <p data-testid="cat-loading" className="py-12 text-center text-sm text-slate-500">...</p>
          ) : items.length === 0 ? (
            <div data-testid="cat-empty" className="mt-6 rounded-2xl border border-dashed border-slate-300 py-12 text-center">
              <Package className="mx-auto h-8 w-8 text-slate-300" />
              <p className="mt-2 text-sm font-semibold text-slate-700">{t("no_products")}</p>
            </div>
          ) : (
            <div className="mt-4 grid grid-cols-2 gap-3">
              {items.map((p) => (
                <Link
                  key={p.id}
                  data-testid={`cat-product-${p.id}`}
                  to={`/product/${p.id}`}
                  className="group block overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-slate-100 transition hover:ring-teal-300"
                >
                  <div className="relative aspect-square bg-slate-50">
                    {(p.thumb || p.image) && (
                      <img src={mediaUrl(p.thumb || p.image)} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    )}
                    {p.verified && (
                      <span className="absolute left-2 top-2 flex items-center gap-0.5 rounded-full bg-sky-500/95 px-1.5 py-0.5 text-[9px] font-bold text-white">
                        <BadgeCheck className="h-3 w-3" /> {t("verified")}
                      </span>
                    )}
                    <button
                      data-testid={`cat-wish-${p.id}`}
                      onClick={(e) => toggleWish(e, p.id)}
                      aria-label="Favourite"
                      className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-white/90 shadow transition active:scale-90"
                    >
                      <Heart className={`h-3.5 w-3.5 ${wish.includes(p.id) ? "fill-red-500 text-red-500" : "text-slate-500"}`} />
                    </button>
                  </div>
                  <div className="p-2.5">
                    <p className="line-clamp-2 min-h-[2.1rem] text-xs font-semibold leading-snug text-slate-800">{p.title}</p>
                    <p className="mt-1 text-sm font-extrabold text-slate-900">
                      {fmt(p.price_min)}{p.price_max && p.price_max !== p.price_min ? ` – ${fmt(p.price_max)}` : ""}
                    </p>
                    <p className="mt-0.5 text-[10px] text-slate-400">MOQ: {p.moq || 1} {p.unit || "pieces"}</p>
                    <p className="mt-1 flex items-center gap-1 text-[10px] text-slate-500">
                      <MapPin className="h-3 w-3" /> {p.country || "—"}
                      {p.rating > 0 && (
                        <span className="ml-auto flex items-center gap-0.5 font-semibold text-slate-700">
                          <Star className="h-3 w-3 fill-amber-400 text-amber-400" /> {p.rating}
                        </span>
                      )}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
